import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import {
  Archive,
  CheckCircle2,
  Clock3,
  GripVertical,
  Headset,
  Inbox,
  Paperclip,
  RotateCw,
  Timer,
  Trash2,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { Loader } from "@/components/ui/Loader";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";
import { useChamadosAutoRefresh } from "@/hooks/useChamadosAutoRefresh";
import {
  archiveChamado,
  fetchChamados,
  updateChamadoStatus,
  type ChamadoDto,
  type ChamadoStatus,
  type ChamadoTipo,
} from "@/services/chamados";
import { cn } from "@/utils/cn";
import { ChamadoDetalheModal } from "./ChamadoDetalheModal";
import { ChamadoLeadTimeModal } from "./ChamadoLeadTimeModal";
import { ChamadosArquivadosModal } from "./ChamadosArquivadosModal";

interface ColumnDefinition {
  status: ChamadoStatus;
  label: string;
  color: string;
}

const COLUMNS: ColumnDefinition[] = [
  {
    status: "esperando_analise",
    label: "Esperando Análise",
    color: "border-amber-200 bg-amber-50 text-amber-800",
  },
  {
    status: "em_analise",
    label: "Em Análise",
    color: "border-blue-200 bg-blue-50 text-blue-800",
  },
  {
    status: "corrigindo",
    label: "Corrigindo",
    color: "border-violet-200 bg-violet-50 text-violet-800",
  },
  {
    status: "esperando_validacao",
    label: "Esperando Validação",
    color: "border-orange-200 bg-orange-50 text-orange-800",
  },
  {
    status: "finalizada",
    label: "Finalizada",
    color: "border-emerald-200 bg-emerald-50 text-emerald-800",
  },
];

const TIPO_LABELS: Record<ChamadoDto["tipo"], string> = {
  bug: "Bug",
  duvida: "Dúvida",
  acesso: "Acesso",
  sugestao: "Sugestão",
};

const TIPO_OPTIONS = Object.entries(TIPO_LABELS).map(([value, label]) => ({ value, label }));

function apiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { error?: unknown } } }).response;
    if (typeof response?.data?.error === "string") return response.data.error;
  }
  return error instanceof Error ? error.message : "Não foi possível carregar os chamados.";
}

/** Dia local no formato YYYY-MM-DD (mesmo formato do filtro de data). */
function toLocalDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Momento em que o chamado entrou no status atual (último status_alterado ou criação). */
function getStatusEnteredAt(chamado: ChamadoDto): string {
  const historico = chamado.historico ?? [];
  for (let i = historico.length - 1; i >= 0; i--) {
    const entry = historico[i];
    if (entry.acao === "status_alterado" || entry.acao === "criado") {
      return entry.createdAt;
    }
  }
  return chamado.createdAt;
}

/** Dia local (YYYY-MM-DD) em que o chamado foi concluído (entrou em "finalizada"). */
function getConclusaoDay(chamado: ChamadoDto): string {
  const date = new Date(getStatusEnteredAt(chamado));
  if (Number.isNaN(date.getTime())) return "";
  return toLocalDay(date);
}

/** Formata duração decorrida em português (ex.: "2d 4h", "3h 15min", "45 min"). */
function formatTempoNaColuna(fromIso: string, nowMs = Date.now()): string {
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from)) return "—";
  const minutes = Math.floor(Math.max(0, nowMs - from) / 60_000);
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) {
    return remMin > 0 ? `${hours}h ${remMin}min` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

const STATUS_COM_TEMPO: ReadonlySet<ChamadoStatus> = new Set([
  "esperando_analise",
  "em_analise",
  "corrigindo",
  "esperando_validacao",
]);

function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg", color)}>{icon}</div>
      <div>
        <p className="text-2xl font-bold leading-none text-slate-800">{value}</p>
        <p className="mt-1 text-xs font-medium text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function ChamadoCard({
  chamado,
  index,
  canEdit,
  nowMs,
  onArchive,
  onOpenDetails,
  onOpenLeadTime,
}: {
  chamado: ChamadoDto;
  index: number;
  canEdit: boolean;
  nowMs: number;
  onArchive: (chamado: ChamadoDto) => void;
  onOpenDetails: (chamado: ChamadoDto) => void;
  onOpenLeadTime: (chamado: ChamadoDto) => void;
}) {
  const showTempo = STATUS_COM_TEMPO.has(chamado.status);
  const tempoNaColuna = showTempo
    ? formatTempoNaColuna(getStatusEnteredAt(chamado), nowMs)
    : null;

  return (
    <Draggable draggableId={chamado.id} index={index} isDragDisabled={!canEdit}>
      {(provided, snapshot) => (
        <article
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={cn(
            "rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow",
            snapshot.isDragging && "rotate-1 shadow-xl ring-2 ring-brand-200",
          )}
        >
          <div className="flex items-start gap-2">
            {canEdit && <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold leading-snug text-slate-800">
                  {chamado.titulo}
                </h3>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    title="Ver conteúdo do chamado"
                    aria-label={`Ver conteúdo do chamado ${chamado.titulo}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenDetails(chamado);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    className="rounded-md p-1 text-brand-600 transition-colors hover:bg-brand-50 hover:text-brand-700"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    title="Ver lead time do chamado"
                    aria-label={`Ver lead time do chamado ${chamado.titulo}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenLeadTime(chamado);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    className="rounded-md p-1 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-700"
                  >
                    <Timer className="h-4 w-4" />
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      title="Excluir e enviar para os arquivos"
                      aria-label={`Excluir chamado ${chamado.titulo}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onArchive(chamado);
                      }}
                      onMouseDown={(event) => event.stopPropagation()}
                      className="rounded-md p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-1.5 text-xs text-slate-500">
                <p className="flex items-center gap-1.5">
                  <UserRound className="h-3.5 w-3.5" />
                  <span className="truncate">{chamado.autorNome}</span>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                    {chamado.modulo || "Sem módulo"}
                  </span>
                  <span className="rounded-full bg-brand-50 px-2 py-0.5 font-medium text-brand-700">
                    {TIPO_LABELS[chamado.tipo] ?? chamado.tipo}
                  </span>
                </div>
                {tempoNaColuna && (
                  <p
                    className="flex items-center gap-1.5 font-medium text-slate-600"
                    title="Tempo nesta coluna"
                  >
                    <Clock3 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span>{tempoNaColuna} nesta coluna</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </article>
      )}
    </Draggable>
  );
}

export function ChamadosTiPage() {
  const { user } = useAuth();
  const canEdit = user?.isTi === true || user?.role === "admin";
  const nowMs = useNow();
  const [chamados, setChamados] = useState<ChamadoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [tipo, setTipo] = useState<ChamadoTipo | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [archivesOpen, setArchivesOpen] = useState(false);
  const [selectedChamadoId, setSelectedChamadoId] = useState<string | null>(null);
  const [leadTimeChamado, setLeadTimeChamado] = useState<ChamadoDto | null>(null);

  const loadChamados = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) setError("");
    try {
      setChamados(await fetchChamados({ tipo, dateFrom, dateTo }));
    } catch (loadError) {
      if (!silent) setError(apiErrorMessage(loadError));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tipo, dateFrom, dateTo]);

  useChamadosAutoRefresh(
    loadChamados,
    dragging || moving || archivesOpen || selectedChamadoId != null || leadTimeChamado != null,
  );

  useEffect(() => {
    setLoading(true);
    void loadChamados();
  }, [loadChamados]);

  const byStatus = useMemo(() => {
    const grouped = Object.fromEntries(
      COLUMNS.map((column) => [column.status, [] as ChamadoDto[]]),
    ) as Record<ChamadoStatus, ChamadoDto[]>;
    // Na coluna Finalizada só entram os chamados concluídos no período do
    // filtro de data; sem filtro, apenas os concluídos no dia atual.
    const hasDateFilter = Boolean(dateFrom || dateTo);
    const today = toLocalDay(new Date());
    for (const chamado of chamados) {
      if (chamado.status === "finalizada") {
        const concludedDay = getConclusaoDay(chamado);
        const inRange = hasDateFilter
          ? (!dateFrom || concludedDay >= dateFrom) && (!dateTo || concludedDay <= dateTo)
          : concludedDay === today;
        if (!inRange) continue;
      }
      grouped[chamado.status]?.push(chamado);
    }
    return grouped;
  }, [chamados, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const today = toLocalDay(new Date());
    return {
      abertos: chamados.filter((item) => item.status !== "finalizada").length,
      aguardando: chamados.filter((item) => item.status === "esperando_analise").length,
      concluidosHoje: chamados.filter(
        (item) => item.status === "finalizada" && getConclusaoDay(item) === today,
      ).length,
    };
  }, [chamados]);

  const handleDragEnd = async (result: DropResult) => {
    if (!canEdit || !result.destination || moving) return;
    const nextStatus = result.destination.droppableId as ChamadoStatus;
    const previousStatus = result.source.droppableId as ChamadoStatus;
    if (nextStatus === previousStatus) return;

    const previous = chamados;
    const movedAt = new Date().toISOString();
    setChamados((current) =>
      current.map((item) => {
        if (item.id !== result.draggableId) return item;
        return {
          ...item,
          status: nextStatus,
          updatedAt: movedAt,
          historico: [
            ...(item.historico ?? []),
            {
              id: `optimistic-${movedAt}`,
              acao: "status_alterado" as const,
              descricao: `Status alterado de ${previousStatus} para ${nextStatus}.`,
              autorId: user?.id ?? "",
              autorNome: user?.displayName ?? "Sistema",
              createdAt: movedAt,
            },
          ],
        };
      }),
    );
    setMoving(true);
    setError("");

    try {
      await updateChamadoStatus(result.draggableId, nextStatus);
      await loadChamados();
    } catch (moveError) {
      setChamados(previous);
      setError(apiErrorMessage(moveError));
    } finally {
      setMoving(false);
    }
  };

  const handleArchive = async (chamado: ChamadoDto) => {
    if (
      !canEdit ||
      !window.confirm(
        `Excluir o chamado "${chamado.titulo}"? Ele será preservado na área de arquivos.`,
      )
    ) {
      return;
    }
    setError("");
    try {
      await archiveChamado(chamado.id);
      setChamados((current) => current.filter((item) => item.id !== chamado.id));
    } catch (archiveError) {
      setError(apiErrorMessage(archiveError));
    }
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
            <Headset className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Chamados TI</h1>
            <p className="text-sm text-slate-500">
              {canEdit
                ? "Gestão de suporte e incidentes internos"
                : "Acompanhe o andamento dos seus chamados"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setArchivesOpen(true)}
              leftIcon={<Archive className="h-4 w-4" />}
            >
              Arquivos
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={loading}
            title="Atualização automática a cada 30s; use para forçar agora"
            onClick={() => {
              setLoading(true);
              void loadChamados();
            }}
            leftIcon={<RotateCw className="h-4 w-4" />}
          >
            Atualizar
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:grid-cols-3">
        <Select
          label="Tipo de chamado"
          placeholder="Todos os tipos"
          options={TIPO_OPTIONS}
          value={tipo}
          onChange={(event) => setTipo(event.target.value as ChamadoTipo | "")}
        />
        <DateInput label="Data inicial" value={dateFrom} onChange={setDateFrom} />
        <DateInput label="Data final" value={dateTo} onChange={setDateTo} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Aguardando Análise"
          value={stats.aguardando}
          icon={<Clock3 className="h-5 w-5" />}
          color="bg-amber-100 text-amber-700"
        />
        <StatCard
          label="Chamados Abertos"
          value={stats.abertos}
          icon={<Inbox className="h-5 w-5" />}
          color="bg-blue-100 text-blue-700"
        />
        <StatCard
          label="Concluídos Hoje"
          value={stats.concluidosHoje}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="bg-emerald-100 text-emerald-700"
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading && chamados.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-16">
          <Loader size="lg" label="Carregando chamados..." />
        </div>
      ) : (
        <DragDropContext
          onDragStart={() => setDragging(true)}
          onDragEnd={(result) => {
            setDragging(false);
            void handleDragEnd(result);
          }}
        >
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {COLUMNS.map((column) => {
              const cards = byStatus[column.status];
              return (
                <div key={column.status} className="flex min-h-0 flex-col">
                  <div
                    className={cn(
                      "mb-2 flex items-center justify-between rounded-lg border px-3 py-2",
                      column.color,
                    )}
                  >
                    <h2 className="text-xs font-bold uppercase tracking-wide">{column.label}</h2>
                    <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-bold">
                      {cards.length}
                    </span>
                  </div>

                  <Droppable droppableId={column.status} isDropDisabled={!canEdit}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={cn(
                          "min-h-32 flex-1 space-y-2 overflow-y-auto rounded-lg border border-dashed p-2 transition-colors",
                          "max-h-[min(60vh,38rem)] [scrollbar-gutter:stable]",
                          snapshot.isDraggingOver
                            ? "border-brand-300 bg-brand-50/60"
                            : "border-slate-200 bg-slate-50/60",
                          moving && "pointer-events-none opacity-70",
                        )}
                      >
                        {cards.map((chamado, index) => (
                          <ChamadoCard
                            key={chamado.id}
                            chamado={chamado}
                            index={index}
                            canEdit={canEdit}
                            nowMs={nowMs}
                            onArchive={(item) => void handleArchive(item)}
                            onOpenDetails={(item) => setSelectedChamadoId(item.id)}
                            onOpenLeadTime={(item) => setLeadTimeChamado(item)}
                          />
                        ))}
                        {provided.placeholder}
                        {cards.length === 0 && (
                          <p className="py-8 text-center text-xs text-slate-400">
                            {canEdit ? "Arraste chamados para cá" : "Nenhum chamado nesta etapa"}
                          </p>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}
      {canEdit && (
        <ChamadosArquivadosModal
          open={archivesOpen}
          onClose={() => setArchivesOpen(false)}
        />
      )}
      <ChamadoDetalheModal
        chamadoId={selectedChamadoId}
        open={selectedChamadoId != null}
        onClose={() => setSelectedChamadoId(null)}
      />
      <ChamadoLeadTimeModal
        chamado={leadTimeChamado}
        open={leadTimeChamado != null}
        onClose={() => setLeadTimeChamado(null)}
      />
    </section>
  );
}
